import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/types';
import { escapeIlike } from '@/lib/api/profiles';

type BusinessRow = Database['public']['Tables']['businesses']['Row'];
export type Business = BusinessRow;

export type CreateBusinessInput = {
  name: string;
  slug: string;
  tagline?: string;
  description?: string;
  website?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  industry?: string;
  category: string;
  owner_profile_id: string;
};

export type BusinessFilter = {
  search?: string;
  categories?: string[];
  countries?: string[];
};

const PAGE_SIZE = 24;

const LIST_COLS =
  'id,slug,name,tagline,logo_url,city,country,industry,category,verified,owner_profile_id,created_at';

const DETAIL_COLS =
  'id,slug,name,tagline,description,logo_url,cover_url,website,phone,address,city,country,industry,category,verified,owner_profile_id,created_at';

export async function listBusinesses(
  filter: BusinessFilter,
  page: number,
): Promise<{ items: Business[]; hasMore: boolean }> {
  let query = supabase
    .from('businesses')
    .select(LIST_COLS)
    .order('created_at', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (filter.search) {
    const term = escapeIlike(filter.search);
    query = query.or(
      `name.ilike.%${term}%,tagline.ilike.%${term}%,city.ilike.%${term}%`,
    );
  }
  if (filter.categories?.length) {
    query = query.in('category', filter.categories);
  }
  if (filter.countries?.length) {
    query = query.in('country', filter.countries);
  }

  const { data } = await query.throwOnError();
  const items = (data ?? []) as Business[];
  return { items, hasMore: items.length === PAGE_SIZE };
}

export async function getBusiness(slug: string): Promise<Business | null> {
  const { data } = await supabase
    .from('businesses')
    .select(DETAIL_COLS)
    .eq('slug', slug)
    .maybeSingle()
    .throwOnError();
  return data as Business | null;
}

export async function createBusiness(input: CreateBusinessInput): Promise<string> {
  const { data } = await supabase
    .from('businesses')
    .insert(input)
    .select('slug')
    .single()
    .throwOnError();
  return (data as { slug: string }).slug;
}

export async function deleteBusiness(businessId: string): Promise<void> {
  await supabase.from('businesses').delete().eq('id', businessId).throwOnError();
}

export type UpdateBusinessInput = {
  name: string;
  tagline?: string | null;
  description?: string | null;
  category: string;
  industry?: string | null;
  city?: string | null;
  country?: string | null;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  logo_url?: string | null;
  cover_url?: string | null;
  verified: boolean;
};

export async function updateBusiness(id: string, input: UpdateBusinessInput): Promise<Business> {
  const { data } = await supabase
    .from('businesses')
    .update(input)
    .eq('id', id)
    .select(DETAIL_COLS)
    .single()
    .throwOnError();
  return data as Business;
}

// ── Map support ───────────────────────────────────────────────────────────────

export type MapBusiness = {
  id: string;
  slug: string;
  name: string;
  category: string;
  city: string | null;
  country: string | null;
  lat: number;
  lng: number;
  // 'member' = member-claimed (verified or owner-created); 'google_places' =
  // auto-scraped. Drives the visually-distinct marker treatment on the map.
  source: string;
  verified: boolean;
  // Canonical Google Maps deep link for scraped places (null for member rows).
  // Surfaced in the InfoWindow as the source-of-truth "View on Google Maps".
  external_url: string | null;
};

// Centroids for the curated CITIES / COUNTRIES lists in constants.ts.
// Businesses whose city matches get a city-level pin; others fall back to
// country centroid. Businesses with neither are excluded from the map.
const CITY_COORDS: Record<string, [number, number]> = {
  'Phnom Penh':         [11.5564, 104.9282],
  'Siem Reap':          [13.3671, 103.8448],
  'Battambang':         [13.0957, 103.2022],
  'Sihanoukville':      [10.6101, 103.5229],
  'Long Beach':         [33.7701, -118.1937],
  'Los Angeles':        [34.0522, -118.2437],
  'Oakland':            [37.8044, -122.2712],
  'San Francisco':      [37.7749, -122.4194],
  'San Jose':           [37.3382, -121.8863],
  'Seattle':            [47.6062, -122.3321],
  'Portland':           [45.5051, -122.6750],
  'Houston':            [29.7604,  -95.3698],
  'Dallas':             [32.7767,  -96.7970],
  'Chicago':            [41.8781,  -87.6298],
  'New York':           [40.7128,  -74.0060],
  'Washington, D.C.':   [38.9072,  -77.0369],
  'Atlanta':            [33.7490,  -84.3880],
  'Boston':             [42.3601,  -71.0589],
  'Toronto':            [43.6532,  -79.3832],
  'Montreal':           [45.5017,  -73.5673],
  'Vancouver':          [49.2827, -123.1207],
  'Paris':              [48.8566,    2.3522],
  'Lyon':               [45.7640,    4.8357],
  'London':             [51.5074,   -0.1278],
  'Berlin':             [52.5200,   13.4050],
  'Sydney':             [-33.8688, 151.2093],
  'Melbourne':          [-37.8136, 144.9631],
  'Tokyo':              [35.6762,  139.6503],
  'Seoul':              [37.5665,  126.9780],
  'Singapore':          [ 1.3521,  103.8198],
  'Bangkok':            [13.7563,  100.5018],
  'Hanoi':              [21.0285,  105.8542],
  'Ho Chi Minh City':   [10.8231,  106.6297],
};

const COUNTRY_COORDS: Record<string, [number, number]> = {
  'Cambodia':       [12.5657,  104.9910],
  'United States':  [37.0902,  -95.7129],
  'France':         [46.2276,    2.2137],
  'Australia':      [-25.2744, 133.7751],
  'Canada':         [56.1304, -106.3468],
  'United Kingdom': [55.3781,   -3.4360],
  'Thailand':       [15.8700,  100.9925],
  'Vietnam':        [14.0583,  108.2772],
  'South Korea':    [35.9078,  127.7669],
  'Japan':          [36.2048,  138.2529],
  'Singapore':      [ 1.3521,  103.8198],
  'Germany':        [51.1657,   10.4515],
  'New Zealand':    [-40.9006, 174.8860],
  'Netherlands':    [52.1326,    5.2913],
  'Malaysia':       [ 4.2105,  101.9758],
};

const MAP_COLS =
  'id,slug,name,category,city,country,lat,lng,source,verified,external_url';

// Page size for the map sweep. Supabase caps any single response at 1000 rows,
// so we paginate to guarantee member businesses are never silently dropped once
// the auto-scraped catalogue grows past that cap.
const MAP_PAGE_SIZE = 1000;

export async function listMapBusinesses(): Promise<MapBusiness[]> {
  const out: MapBusiness[] = [];

  for (let from = 0; ; from += MAP_PAGE_SIZE) {
    const { data } = await supabase
      .from('businesses')
      .select(MAP_COLS)
      // Ordering ensures member/claimed rows are returned first, so even a
      // (defensive) future cap can never drop them in favour of scraped rows:
      // 'member' > 'google_places' alphabetically, so DESC puts member first;
      // then verified, then owned, then a stable tiebreak.
      .order('source', { ascending: false })
      .order('verified', { ascending: false })
      .order('owner_profile_id', { ascending: false, nullsFirst: false })
      .order('id', { ascending: true })
      .range(from, from + MAP_PAGE_SIZE - 1)
      .throwOnError();

    const rows = data ?? [];
    for (const biz of rows) {
      // Prefer real coordinates (Google Places businesses always carry them);
      // fall back to the curated city/country centroid for member businesses
      // that predate the lat/lng columns. Skip anything we can't place.
      let lat = biz.lat;
      let lng = biz.lng;
      if (lat == null || lng == null) {
        const coords =
          (biz.city && CITY_COORDS[biz.city]) ||
          (biz.country && COUNTRY_COORDS[biz.country]);
        if (!coords) continue;
        [lat, lng] = coords;
      }
      out.push({
        id: biz.id,
        slug: biz.slug,
        name: biz.name,
        category: biz.category,
        city: biz.city,
        country: biz.country,
        lat,
        lng,
        source: biz.source ?? 'member',
        verified: biz.verified,
        external_url: biz.external_url ?? null,
      });
    }

    if (rows.length < MAP_PAGE_SIZE) break;
  }

  return out;
}

export function slugifyBusinessName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 160);
}
